import React from "react";
import { Head, Link, usePage, router } from "@inertiajs/react";
import styled from "styled-components";
import { GlobalStyle } from "../../constants/global-style";
import { apiPost } from "../../api";
import { brand_danger, brand_orange_deep, dark, narrowScreenMediaQuery } from "../../constants/styles";
import AuthLayout from "../../auth/AuthLayout";
import FormField, { Control } from "../../auth/FormField";
import PasswordField from "../../auth/PasswordField";
import SelectField from "../../auth/SelectField";
import SubmitButton from "../../auth/SubmitButton";
import InlineAlert from "../../auth/InlineAlert";
import GoogleSignInButton from "../../auth/GoogleSignInButton";
import PasswordStrengthHint, { validatePassword } from "../../auth/PasswordStrengthHint";
import useAuthForm, { normalizeServerErrors } from "../../auth/useAuthForm";

// Standalone signup page. Handles:
//   S1 email already used        — field-level error + top banner
//   S2 password below criteria    — live strength hint + field error
//   S3 signup success             — router.visit to /signup/check-inbox
//   S4 (handled on ConfirmEmail)  — separate page, not wired here

const Heading = styled.h1`
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0 0 8px;
  color: #111827;

  [data-theme="dark"] & {
    color: ${dark.text_primary};
  }
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.7;

  [data-theme="dark"] & {
    color: ${dark.text_secondary};
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const BaseError = styled.div`
  color: ${brand_danger};
  font-size: 13px;
  font-weight: 500;
`;

// Outlined secondary CTA — mirrors Login's `DaftarButton`. Rendered below
// the primary "Daftar" submit so users who already have an account can jump
// straight to /login without hunting for the header cross-link.
const MasukButton = styled(Link)`
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  padding: 12px 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px solid ${brand_orange_deep};
  border-radius: 12px;
  background: transparent;
  color: ${brand_orange_deep};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-decoration: none;
  transition: background 150ms ease, color 150ms ease, transform 120ms ease, box-shadow 150ms ease;

  &:hover {
    background: ${brand_orange_deep};
    color: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(216, 89, 7, 0.28);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 3px;
  }

  [data-theme="dark"] & {
    border-color: #fdba74;
    color: #fdba74;

    &:hover {
      background: #fdba74;
      color: #1f2937;
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #9ca3af;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 4px 0;

  &::before, &::after {
    content: "";
    flex: 1 1 auto;
    height: 1px;
    background: #e5e7eb;
  }

  [data-theme="dark"] & {
    color: ${dark.text_muted};

    &::before, &::after { background: ${dark.border}; }
  }
`;

const POSITION_OPTIONS = [
  { value: "pegawai", label: "Pegawai" },
  { value: "mahasiswa", label: "Mahasiswa" },
  { value: "jobseeker", label: "Jobseeker" },
  { value: "wirausaha", label: "Wirausaha" },
  { value: "lainnya", label: "Lainnya" },
];

export default function Signup() {
  const { redirect_after_login: redirectAfterLogin } = usePage().props;

  const {
    fields, setField, errors, mergeErrors, setErrors,
    submitting, setSubmitting,
  } = useAuthForm({
    full_name: "",
    email: "",
    phone_number: "",
    position: "",
    company: "",
    business_field: "",
    city_company: "",
    password: "",
    password_confirmation: "",
  });

  const validateClientSide = () => {
    const errs = {};
    if (!fields.full_name?.trim()) errs.full_name = "Nama lengkap wajib diisi";
    if (!fields.email?.trim()) errs.email = "Email wajib diisi";
    if (!fields.phone_number?.trim()) errs.phone_number = "Nomor telepon wajib diisi";
    if (!fields.password) errs.password = "Password wajib diisi";
    if (!fields.password_confirmation) errs.password_confirmation = "Konfirmasi password wajib diisi";

    if (fields.password && fields.password_confirmation && fields.password !== fields.password_confirmation) {
      errs.password_confirmation = "Password tidak cocok";
    }

    if (fields.password) {
      const checks = validatePassword(fields.password);
      if (!(checks.length && checks.digit && checks.upper && checks.symbol)) {
        errs.password = "Password belum memenuhi kriteria";
      }
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const clientErrs = validateClientSide();
    if (Object.keys(clientErrs).length > 0) {
      setErrors(clientErrs);
      return;
    }

    setSubmitting(true);
    setErrors({});
    try {
      const { parsedResponse, statusCode } = await apiPost("/authentications/registrations", {
        body: {
          user: {
            full_name: fields.full_name,
            email: fields.email,
            phone_number: fields.phone_number,
            position: fields.position || null,
            company: fields.company || null,
            business_field: fields.business_field || null,
            city_company: fields.city_company || null,
            password: fields.password,
            password_confirmation: fields.password_confirmation,
          },
          redirect_after_login: redirectAfterLogin || undefined,
        },
      });

      if (statusCode >= 200 && statusCode < 300 && parsedResponse?.location_url) {
        router.visit(parsedResponse.location_url);
        return;
      }

      const serverErrs = normalizeServerErrors(parsedResponse?.errors);
      mergeErrors({
        ...serverErrs,
        base: serverErrs.base || parsedResponse?.alert || "Pendaftaran gagal.",
      });
    } catch (err) {
      if (typeof console !== "undefined") console.warn("[Signup] submit failed", err);
      mergeErrors({ base: "Terjadi kesalahan. Silakan coba lagi." });
    } finally {
      setSubmitting(false);
    }
  };

  const [confirmationTouched, setConfirmationTouched] = React.useState(false);
  const confirmationLive =
    confirmationTouched &&
    fields.password_confirmation &&
    fields.password !== fields.password_confirmation
      ? "Password tidak cocok"
      : errors.password_confirmation;

  return (
    <>
      <Head>
        <title>Daftar — Taxsam Learning Center</title>
        <meta
          name="description"
          content="Daftar akun Taxsam Learning Center — akses pelatihan sertifikasi pajak Brevet, webinar, dan materi pembelajaran pajak terlengkap."
        />
      </Head>
      <GlobalStyle />
      <AuthLayout
        imageSide="right"
        crossLink={{
          prompt: "Sudah punya akun?",
          label: "Masuk",
          href: `/login${redirectAfterLogin ? `?redirect_after_login=${encodeURIComponent(redirectAfterLogin)}` : ""}`,
        }}
      >
        <Heading>Daftar</Heading>
        <Subtitle>
          Rasakan pengalaman pelatihan sertifikasi Brevet paling mudah, fleksibel, dan terjangkau hanya di TLC!
        </Subtitle>

        {errors.base && (
          <InlineAlert variant="danger" title="Pendaftaran gagal">{errors.base}</InlineAlert>
        )}

        <GoogleSignInButton proceedTo={undefined} label="Daftar dengan Google" />

        <Divider>Atau</Divider>

        <form onSubmit={handleSubmit} noValidate>
          <Grid>
            <FormField id="signup-full-name" label="Nama Lengkap" required error={errors.full_name}>
              <Control
                type="text"
                value={fields.full_name}
                onChange={(e) => setField("full_name", e.target.value)}
                placeholder="Nama sesuai identitas"
                autoComplete="name"
                required
              />
            </FormField>

            <FormField id="signup-email" label="Email" required error={errors.email}>
              <Control
                type="email"
                value={fields.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="mail@abc.com"
                autoComplete="email"
                required
              />
            </FormField>

            <FormField id="signup-phone" label="Nomor Telepon" required error={errors.phone_number}>
              <Control
                type="tel"
                inputMode="tel"
                value={fields.phone_number}
                onChange={(e) => setField("phone_number", e.target.value)}
                placeholder="08123456789"
                autoComplete="tel"
                required
              />
            </FormField>

            <SelectField
              id="signup-position"
              label="Profile Pekerjaan"
              value={fields.position}
              onChange={(e) => setField("position", e.target.value)}
              options={POSITION_OPTIONS}
              placeholder="Pilih profil pekerjaan"
              error={errors.position}
            />

            <FormField id="signup-company" label="Nama Perusahaan" error={errors.company}>
              <Control
                type="text"
                value={fields.company}
                onChange={(e) => setField("company", e.target.value)}
                placeholder="Opsional"
                autoComplete="organization"
              />
            </FormField>

            <FormField id="signup-business-field" label="Bidang Usaha/Pekerjaan" error={errors.business_field}>
              <Control
                type="text"
                value={fields.business_field}
                onChange={(e) => setField("business_field", e.target.value)}
                placeholder="Opsional"
              />
            </FormField>

            <FullRow>
              <FormField id="signup-city" label="Kabupaten/Kota" error={errors.city_company}>
                <Control
                  type="text"
                  value={fields.city_company}
                  onChange={(e) => setField("city_company", e.target.value)}
                  placeholder="Opsional"
                  autoComplete="address-level2"
                />
              </FormField>
            </FullRow>

            <FullRow>
              <PasswordField
                id="signup-password"
                label="Password"
                required
                value={fields.password}
                onChange={(e) => setField("password", e.target.value)}
                error={errors.password}
                placeholder="Minimal 6 karakter, huruf kapital, angka, simbol"
                autoComplete="new-password"
              />
              <PasswordStrengthHint value={fields.password} />
            </FullRow>

            <FullRow>
              <PasswordField
                id="signup-password-confirmation"
                label="Konfirmasi Password"
                required
                value={fields.password_confirmation}
                onChange={(e) => setField("password_confirmation", e.target.value)}
                onBlur={() => setConfirmationTouched(true)}
                error={confirmationLive}
                placeholder="Ulangi password"
                autoComplete="new-password"
              />
            </FullRow>

            <FullRow>
              <SubmitButton submitting={submitting}>
                {submitting ? "Memproses…" : "Daftar"}
              </SubmitButton>
            </FullRow>

            <FullRow>
              <MasukButton
                href={`/login${redirectAfterLogin ? `?redirect_after_login=${encodeURIComponent(redirectAfterLogin)}` : ""}`}
              >
                Sudah punya akun? Masuk
              </MasukButton>
            </FullRow>
          </Grid>
        </form>
      </AuthLayout>
    </>
  );
}
